import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from '@paladin-monorepo/common';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LinkedinIcon } from '@/components/icons/LinkedinIcon';
import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { linkedinApi } from '@/lib/api/linkedin';
import { googleApi } from '@/lib/api/google';
import { useAuth } from '@/lib/auth';

export function ConnectedAccountsCard() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { connectLinkedIn, connectGoogle } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'ConnectedAccountsCard',
  });

  const dateLocale = i18n.language?.startsWith('en') ? 'en-GB' : 'cs-CZ';

  const linkedinQuery = useQuery({
    queryKey: ['/me/linkedin/status'],
    queryFn: () => linkedinApi.getConnectionStatus(),
  });

  const googleQuery = useQuery({
    queryKey: ['/me/google/status'],
    queryFn: () => googleApi.getConnectionStatus(),
  });

  const linkedinDisconnect = useMutation({
    mutationFn: () => linkedinApi.disconnect(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/me/linkedin/status'] });
      toast.success(t('settings.connectedAccounts.linkedin.disconnectSuccess'));
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.data?.code === ErrorCode.LinkedinOnlySignInMethod) {
        toast.error(t('settings.connectedAccounts.linkedin.disconnectBlocked'));
        return;
      }
      toast.error(t('settings.connectedAccounts.linkedin.disconnectError'));
    },
  });

  const googleDisconnect = useMutation({
    mutationFn: () => googleApi.disconnect(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/me/google/status'] });
      toast.success(t('settings.connectedAccounts.google.disconnectSuccess'));
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.data?.code === ErrorCode.GoogleOnlySignInMethod) {
        toast.error(t('settings.connectedAccounts.google.disconnectBlocked'));
        return;
      }
      toast.error(t('settings.connectedAccounts.google.disconnectError'));
    },
  });

  const linkedinDate =
    linkedinQuery.data?.connectedAt != null
      ? new Date(linkedinQuery.data.connectedAt).toLocaleDateString(dateLocale)
      : null;

  const googleDate =
    googleQuery.data?.connectedAt != null
      ? new Date(googleQuery.data.connectedAt).toLocaleDateString(dateLocale)
      : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <LinkedinIcon className="h-5 w-5 text-[#0A66C2]" />
            {t('settings.connectedAccounts.linkedin.title')}
          </CardTitle>
          <CardDescription>{t('settings.connectedAccounts.linkedin.description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-brand-secondary">
            {linkedinQuery.isLoading
              ? '…'
              : linkedinQuery.data?.connected
                ? t('settings.connectedAccounts.linkedin.statusConnected', { date: linkedinDate })
                : t('settings.connectedAccounts.linkedin.statusDisconnected')}
          </p>
          {linkedinQuery.data?.connected ? (
            <Button
              type="button"
              variant="outline"
              disabled={linkedinDisconnect.isPending || linkedinQuery.isLoading}
              onClick={() => linkedinDisconnect.mutate()}
            >
              {t('settings.connectedAccounts.linkedin.disconnect')}
            </Button>
          ) : (
            <Button type="button" disabled={linkedinQuery.isLoading} onClick={() => connectLinkedIn()}>
              {t('settings.connectedAccounts.linkedin.connect')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <GoogleIcon className="h-5 w-5" />
            {t('settings.connectedAccounts.google.title')}
          </CardTitle>
          <CardDescription>{t('settings.connectedAccounts.google.description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-brand-secondary">
            {googleQuery.isLoading
              ? '…'
              : googleQuery.data?.connected
                ? t('settings.connectedAccounts.google.statusConnected', { date: googleDate })
                : t('settings.connectedAccounts.google.statusDisconnected')}
          </p>
          {googleQuery.data?.connected ? (
            <Button
              type="button"
              variant="outline"
              disabled={googleDisconnect.isPending || googleQuery.isLoading}
              onClick={() => googleDisconnect.mutate()}
            >
              {t('settings.connectedAccounts.google.disconnect')}
            </Button>
          ) : (
            <Button type="button" disabled={googleQuery.isLoading} onClick={() => connectGoogle()}>
              {t('settings.connectedAccounts.google.connect')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
